import { FC, useEffect, useMemo, useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import { SaveButton } from "components/buttons/SaveButton"
import { ColorPickerField } from "components/inputs/ColorPickerField"
import { DropdownMenu } from "components/inputs/DropdownMenu"
import { InputField } from "components/inputs/InputField"
import { TextareaField } from "components/inputs/TextareaField"
import { TitledCheckbox } from "components/inputs/TitledCheckbox"
import { slugify } from "components/promotions/BlocksEditor/helpers/slugify"

import { RuleBuilder } from "features/tags/RuleBuilder"
import { createEmptyGroup, RuleGroup } from "features/tags/types"

import { RuleFieldDef, TagRule } from "services/tags/type"

type NewAutoTagState = {
  name: string
  slug: string
  color: string
  category: string
  priority: number
  description: string
}

export type LinkedTag = {
  id: string
  name: string
  slug: string
  color?: string | null
  category?: string | null
}

type EditableTagState = {
  name: string
  color: string
  category: string
}

const DEFAULT_TAG_COLOR = "#22c55e"

const EMPTY_NEW_TAG: NewAutoTagState = {
  name: "",
  slug: "",
  color: DEFAULT_TAG_COLOR,
  category: "",
  priority: 0,
  description: ""
}

const tagStateOf = (tag: LinkedTag | null): EditableTagState => ({
  name: tag?.name ?? "",
  color: tag?.color ?? DEFAULT_TAG_COLOR,
  category: tag?.category ?? ""
})

type Props = {
  saving: boolean
  fields: RuleFieldDef[]
  editRule: TagRule | null
  linkedTag: LinkedTag | null
  onSave: (payload: any) => Promise<void>
}

export const AutoRuleTab: FC<Props> = ({ saving, fields, editRule, linkedTag, onSave }) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const isEdit = !!editRule?.id

  const [ruleName, setRuleName] = useState("")
  const [ruleActive, setRuleActive] = useState(true)
  const [rulePriority, setRulePriority] = useState(0)
  const [evaluationMode, setEvaluationMode] = useState<"BATCH" | "REALTIME">("BATCH")
  const [conditions, setConditions] = useState<RuleGroup>(createEmptyGroup("all"))
  const [tagEdit, setTagEdit] = useState<EditableTagState>(tagStateOf(null))
  const [newTag, setNewTag] = useState<NewAutoTagState>(EMPTY_NEW_TAG)

  const autoSlugEditedRef = useRef(false)

  // Only BATCH is picked up by the cron; anything else would never run (the API rejects it).
  const modeOptions = useMemo(() => [{ name: "BATCH", value: "BATCH" }], [])

  useEffect(() => {
    if (editRule) {
      setRuleName(editRule.name ?? "")
      setRuleActive(!!editRule.isActive)
      setRulePriority(editRule.priority ?? 0)
      setEvaluationMode((String(editRule.evaluationMode).toUpperCase() as any) || "BATCH")
      setConditions((editRule.conditions as any) ?? createEmptyGroup("all"))

      setNewTag(EMPTY_NEW_TAG)
      setTagEdit(tagStateOf(linkedTag))
      autoSlugEditedRef.current = false
    } else {
      setRuleName("")
      setRuleActive(true)
      setRulePriority(0)
      setEvaluationMode("BATCH")
      setConditions(createEmptyGroup("all"))
      setNewTag(EMPTY_NEW_TAG)
      setTagEdit(tagStateOf(null))
      autoSlugEditedRef.current = false
    }
  }, [editRule, linkedTag])

  const canSave = useMemo(() => {
    if (!ruleName.trim()) return false
    if (isEdit) return !!tagEdit.name.trim()
    return !!newTag.name.trim() && !!newTag.slug.trim()
  }, [ruleName, isEdit, tagEdit.name, newTag.name, newTag.slug])

  const handleSave = () => {
    if (!canSave) return

    const payload: any = {
      name: ruleName.trim(),
      isActive: ruleActive,
      priority: rulePriority,
      evaluationMode,
      conditions
    }

    if (isEdit) {
      // Only sent when something actually changed, so an ordinary rule edit stays one write.
      // Slug is deliberately absent: bonus eligibility stores tag SLUGS, so re-slugging a
      // rename would silently detach every bonus targeting this tag (the API rejects it too).
      const base = tagStateOf(linkedTag)
      const tagDirty =
        tagEdit.name.trim() !== base.name || tagEdit.color !== base.color || tagEdit.category.trim() !== (base.category ?? "")
      if (tagDirty && linkedTag) {
        payload.tag = {
          id: linkedTag.id,
          name: tagEdit.name.trim(),
          color: tagEdit.color,
          category: tagEdit.category.trim() ? tagEdit.category.trim() : null
        }
      }
    } else {
      payload.tag = {
        name: newTag.name.trim(),
        slug: slugify((newTag.slug || newTag.name).trim()),
        color: newTag.color,
        isAutomatic: true,
        category: newTag.category?.trim() ? newTag.category.trim() : null,
        priority: Number.isFinite(Number(newTag.priority)) ? Number(newTag.priority) : 0,
        description: newTag.description?.trim() ? newTag.description.trim() : null
      }
    }

    onSave(payload)
  }

  return (
    <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="space-y-3">
        <InputField title={`${t("tags.rule.ruleName")} *`} required value={ruleName} onChange={(e: any) => setRuleName(e.target.value)} />

        <div className="flex gap-2">
          <DropdownMenu
            title={t("tags.rule.mode") as string}
            options={modeOptions}
            value={evaluationMode}
            onChange={(e: any) => setEvaluationMode(e.target.value)}
            hideEmptyOption
          />
          <InputField
            title={t("tags.rule.priority") as string}
            type="number"
            value={rulePriority}
            onChange={(e: any) => setRulePriority(Number(e.target.value))}
          />
        </div>

        <TitledCheckbox title={t("tags.rule.active") as string} checked={ruleActive} onChange={(e: any) => setRuleActive(e.target.checked)} />

        <div className="divider">{t("tags.rule.automaticTag")}</div>

        {isEdit ? (
          <div className="space-y-2">
            <InputField
              title={`${t("tags.rule.tagName")} *`}
              required
              value={tagEdit.name}
              onChange={(e: any) => setTagEdit((p) => ({ ...p, name: e.target.value }))}
              hint={t("tags.rule.tagNameHint") as string}
            />

            <InputField
              title={t("tags.rule.tagSlugLocked") as string}
              value={linkedTag?.slug ?? ""}
              disabled
              hint={t("tags.rule.tagSlugHint") as string}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <ColorPickerField
                label={t("tags.rule.color") as string}
                value={tagEdit.color}
                onChange={(hex: string) => setTagEdit((p) => ({ ...p, color: hex }))}
              />
              <InputField
                title={t("tags.rule.category") as string}
                value={tagEdit.category}
                onChange={(e: any) => setTagEdit((p) => ({ ...p, category: e.target.value }))}
              />
            </div>

            <div className="text-xs opacity-60">{t("tags.rule.changeTagHint")}</div>
          </div>
        ) : (
          <div className="space-y-2">
            <InputField
              title={`${t("tags.rule.tagName")} *`}
              required
              value={newTag.name}
              onChange={(e: any) => {
                const nextName = e.target.value
                setNewTag((p) => {
                  const next = { ...p, name: nextName }
                  if (!autoSlugEditedRef.current) next.slug = slugify(nextName)
                  return next
                })
              }}
            />

            <InputField
              title={`${t("tags.rule.tagSlug")} *`}
              required
              value={newTag.slug}
              onChange={(e: any) => {
                autoSlugEditedRef.current = true
                setNewTag((p) => ({ ...p, slug: slugify(e.target.value) }))
              }}
              onBlur={() => {
                if (!newTag.slug.trim()) autoSlugEditedRef.current = false
              }}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <ColorPickerField
                label={t("tags.rule.color") as string}
                value={newTag.color}
                onChange={(hex: string) => setNewTag((p) => ({ ...p, color: hex }))}
              />
              <InputField
                title={t("tags.rule.category") as string}
                value={newTag.category}
                onChange={(e: any) => setNewTag((p) => ({ ...p, category: e.target.value }))}
              />
            </div>

            <InputField
              title={t("tags.rule.tagPriority") as string}
              type="number"
              value={newTag.priority}
              onChange={(e: any) => setNewTag((p) => ({ ...p, priority: Number(e.target.value) }))}
            />

            <TextareaField
              title={t("tags.rule.description") as string}
              value={newTag.description}
              onChange={(e) => setNewTag((p) => ({ ...p, description: e.target.value }))}
            />
          </div>
        )}
      </div>

      <div className="lg:col-span-2 space-y-3">
        <div className="text-sm opacity-80">{t("tags.rule.conditions")}</div>
        <RuleBuilder value={conditions} fields={fields} onChange={setConditions} />

        <div className="flex justify-end">
          <SaveButton onClick={handleSave} loading={saving} disabled={!canSave} className="w-auto mr-0">
            {isEdit ? t("tags.rule.saveRule") : t("tags.rule.createRuleAndTag")}
          </SaveButton>
        </div>
      </div>
    </div>
  )
}
